import { createStore } from 'vuex'
import { fetchTimeline, fetchMetadataPage, setStation } from '../services/api'
import audioController from '../services/audioController'
import auth from './auth'

const TIME_RANGES = [
  { label: '5M', ms: 5 * 60 * 1000 },
  { label: '15M', ms: 15 * 60 * 1000 },
  { label: '30M', ms: 30 * 60 * 1000 },
  { label: '1H', ms: 60 * 60 * 1000 },
  { label: '3H', ms: 3 * 60 * 60 * 1000 },
  { label: '6H', ms: 6 * 60 * 60 * 1000 }
]
const DEFAULT_RANGE_INDEX = 2
const SIX_HOURS = 6 * 60 * 60 * 1000

// Initial state factory function for reset
function getInitialState() {
  return {
    station: null,
    timeRanges: TIME_RANGES,
    isLive: true,
    isPlaying: false,
    isBuffering: false,  // True while MSE is loading/buffering
    currentTime: Date.now(),
    volume: 80,
    muted: false,
    viewDuration: TIME_RANGES[DEFAULT_RANGE_INDEX].ms,
    viewStart: Date.now() - TIME_RANGES[DEFAULT_RANGE_INDEX].ms * 0.5,
    viewEnd: Date.now() + TIME_RANGES[DEFAULT_RANGE_INDEX].ms * 0.5,
    viewLocked: false, // When true, view doesn't auto-follow currentTime (user is panning)
    segments: [],
    metadata: [],
    metadataList: [],
    metadataHasMore: true,
    metadataHasMoreNewer: false,
    metadataLoading: false,
    metadataOldest: null,
    metadataNewest: null,
    metadataTotalCount: 0,
    sourceConnected: false,
    wsConnected: false,
    currentSegmentId: null,
    activeMetadataId: null,
    skipActiveMetadataUpdate: false,  // Flag to skip audioController updates during navigation
    selectionMode: false,
    selectionStart: null,
    selectionEnd: null,
    isSelecting: false,
    isExporting: false,
    currentExportId: null,
    exportProgress: 0,
    exportStatus: null,
    draggingHandle: null,
    previewTimeout: null,
    previewType: null,
    // Multi-cut export queue
    exportCuts: [] // Array of { id, start, end }
  }
}

export default createStore({
  state: getInitialState(),

  getters: {
    selectionNormalized(state) {
      if (state.selectionStart === null || state.selectionEnd === null) return null
      return {
        start: Math.min(state.selectionStart, state.selectionEnd),
        end: Math.max(state.selectionStart, state.selectionEnd)
      }
    },
    selectionDuration(state, getters) {
      if (!getters.selectionNormalized) return 0
      return getters.selectionNormalized.end - getters.selectionNormalized.start
    },
    selectionDurationFormatted(state, getters) {
      const ms = getters.selectionDuration
      const minutes = Math.floor(ms / 60000)
      const seconds = Math.floor((ms % 60000) / 1000)
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
    activeRangeIndex(state) {
      for (let i = 0; i < TIME_RANGES.length; i++) {
        if (state.viewDuration <= TIME_RANGES[i].ms) return i
      }
      return TIME_RANGES.length - 1
    },
    canEnterSelectionMode(state) {
      return !state.isLive && !state.isPlaying
    },
    typeCategories(state) {
      return state.station?.typeCategories || []
    },
    defaultTypeColor(state) {
      return state.station?.defaultTypeColor || '#999999'
    },
    stationName(state) {
      return state.station?.name || ''
    },
    // Multi-cut getters
    exportCutsCount(state) {
      return state.exportCuts.length
    },
    exportCutsTotalDuration(state) {
      return state.exportCuts.reduce((total, cut) => total + (cut.end - cut.start), 0)
    },
    exportCutsTotalDurationFormatted(state, getters) {
      const ms = getters.exportCutsTotalDuration
      const hours = Math.floor(ms / 3600000)
      const minutes = Math.floor((ms % 3600000) / 60000)
      const seconds = Math.floor((ms % 60000) / 1000)
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      }
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }
  },

  mutations: {
    // Station management
    SET_STATION(state, station) {
      state.station = station
      if (station) {
        setStation(station.slug, station.liveAudioUrl)
      }
    },
    RESET_STATE(state) {
      // Reset all state except timeRanges
      const initial = getInitialState()
      Object.keys(initial).forEach(key => {
        if (key !== 'timeRanges') {
          state[key] = initial[key]
        }
      })
    },
    SET_LIVE(state, val) { 
      state.isLive = val
      // When going live, unlock the view so it follows
      if (val) state.viewLocked = false
    },
    SET_PLAYING(state, val) { state.isPlaying = val },
    SET_BUFFERING(state, val) { state.isBuffering = val },
    SET_CURRENT_TIME(state, val) { 
      state.currentTime = val
      // Only auto-center view if not locked (user hasn't panned away)
      if (!state.viewLocked) {
        const centerRatio = 0.5
        state.viewStart = val - (state.viewDuration * centerRatio)
        state.viewEnd = val + (state.viewDuration * (1 - centerRatio))
      }
    },
    SET_VIEW_LOCKED(state, val) { state.viewLocked = val },
    SET_VOLUME(state, val) { state.volume = val },
    SET_MUTED(state, val) { state.muted = val },
    SET_VIEW(state, { start, end, duration }) {
      if (duration !== undefined) state.viewDuration = duration
      // If only duration changes, recalculate start/end
      if (duration !== undefined && start === undefined && end === undefined) {
        const centerRatio = 0.5
        // If view is locked, keep the current view center; otherwise use currentTime
        const centerTime = state.viewLocked 
          ? state.viewStart + ((state.viewEnd - state.viewStart) * centerRatio)
          : state.currentTime
        state.viewStart = centerTime - (state.viewDuration * centerRatio)
        state.viewEnd = centerTime + (state.viewDuration * (1 - centerRatio))
      } else {
        if (start !== undefined) state.viewStart = start
        if (end !== undefined) state.viewEnd = end
      }
    },
    SET_SEGMENTS(state, newSegments) { 
      // Merge new segments instead of replacing to avoid unnecessary re-renders
      if (!newSegments || newSegments.length === 0) {
        // Only clear if we actually have segments
        if (state.segments.length > 0) {
          state.segments = []
        }
        return
      }
      
      // Build a set of existing segment IDs for fast lookup
      const existingIds = new Set(state.segments.map(s => s.id))
      const toAdd = newSegments.filter(seg => !existingIds.has(seg.id))

      if (toAdd.length > 0) {
        // Replace array reference so Vue watchers always fire (push() doesn't trigger deep watch)
        state.segments = [...state.segments, ...toAdd]
      }
    },
    ADD_SEGMENT(state, segment) {
      // Add a single segment from WebSocket (live updates)
      if (!segment || !segment.id) return
      // Check if already exists
      if (state.segments.find(s => s.id === segment.id)) return
      
      // Only add if segment falls within current loaded time range
      // This prevents live segments from mixing with historical data
      if (state.segments.length > 0) {
        const oldest = state.segments[0].timestamp
        const newest = state.segments[state.segments.length - 1].timestamp
        const range = newest - oldest
        // Only add if segment is within or just after the loaded range
        if (segment.timestamp < oldest - 60000 || segment.timestamp > newest + range + 60000) {
          return
        }
      }
      
      const newSegments = [...state.segments, segment]
      newSegments.sort((a, b) => a.timestamp - b.timestamp)
      state.segments = newSegments
    },
    UPDATE_SEGMENT(state, { id, updates }) {
      // Update an existing segment (e.g., when peaks are added)
      const index = state.segments.findIndex(s => s.id === id)
      if (index !== -1) {
        // Create new segment object with updates for reactivity
        state.segments[index] = { ...state.segments[index], ...updates }
        // Trigger reactivity by replacing array
        state.segments = [...state.segments]
      }
    },
    SET_METADATA(state, val) { state.metadata = val },
    MERGE_METADATA(state, newMetadata) {
      // Merge new metadata into existing (for timeline rendering)
      for (const meta of newMetadata) {
        if (!state.metadata.find(m => m.id === meta.id)) {
          state.metadata.push(meta)
        }
      }
    },
    SET_METADATA_LIST(state, newList) { 
      // Instead of replacing the array (which causes Vue to re-render everything),
      // update in place to preserve DOM nodes for items that already exist
      const newIds = new Set(newList.map(m => m.id))
      
      // Remove items that are no longer in the new list
      for (let i = state.metadataList.length - 1; i >= 0; i--) {
        if (!newIds.has(state.metadataList[i].id)) {
          state.metadataList.splice(i, 1)
        }
      }
      
      // Add new items and update positions to match new list order
      const currentById = new Map(state.metadataList.map(m => [m.id, m]))
      state.metadataList.length = 0
      for (const item of newList) {
        if (currentById.has(item.id)) {
          // Keep existing object reference to preserve Vue reactivity
          state.metadataList.push(currentById.get(item.id))
        } else {
          state.metadataList.push(item)
        }
      }
    },
    APPEND_METADATA_OLDER(state, items) {
      for (const meta of items) {
        if (!state.metadataList.find(m => m.id === meta.id)) {
          state.metadataList.push(meta)
        }
      }
    },
    PREPEND_METADATA_NEWER(state, items) {
      for (const meta of items.slice().reverse()) {
        if (!state.metadataList.find(m => m.id === meta.id)) {
          state.metadataList.unshift(meta)
        }
      }
    },
    UPDATE_METADATA_TIMESTAMPS(state) {
      if (state.metadataList.length > 0) {
        state.metadataOldest = Math.min(...state.metadataList.map(m => m.timestamp))
        state.metadataNewest = Math.max(...state.metadataList.map(m => m.timestamp))
      }
    },
    SET_METADATA_PAGINATION(state, { hasMore, hasMoreNewer, hasMoreOlder, totalCount }) {
      if (hasMore !== undefined) state.metadataHasMore = hasMore
      if (hasMoreNewer !== undefined) state.metadataHasMoreNewer = hasMoreNewer
      if (hasMoreOlder !== undefined) state.metadataHasMore = hasMoreOlder
      if (totalCount !== undefined) state.metadataTotalCount = totalCount
    },
    SET_METADATA_LOADING(state, val) { state.metadataLoading = val },
    SET_SOURCE_CONNECTED(state, val) { state.sourceConnected = val },
    SET_WS_CONNECTED(state, val) { state.wsConnected = val },
    SET_CURRENT_SEGMENT_ID(state, val) { state.currentSegmentId = val },
    SET_ACTIVE_METADATA_ID(state, val) { state.activeMetadataId = val },
    SET_SKIP_ACTIVE_METADATA_UPDATE(state, val) { state.skipActiveMetadataUpdate = val },
    SET_SELECTION_MODE(state, val) { state.selectionMode = val },
    SET_SELECTION(state, { start, end }) {
      if (start !== undefined) state.selectionStart = start
      if (end !== undefined) state.selectionEnd = end
    },
    SET_IS_SELECTING(state, val) { state.isSelecting = val },
    SET_DRAGGING_HANDLE(state, val) { state.draggingHandle = val },
    CLEAR_SELECTION(state) {
      state.selectionStart = null
      state.selectionEnd = null
      state.selectionMode = false
      state.draggingHandle = null
    },
    SET_EXPORTING(state, { isExporting, exportId, progress, status }) {
      if (isExporting !== undefined) state.isExporting = isExporting
      if (exportId !== undefined) state.currentExportId = exportId
      if (progress !== undefined) state.exportProgress = progress
      if (status !== undefined) state.exportStatus = status
    },
    SET_PREVIEW(state, { type, timeout }) {
      if (type !== undefined) state.previewType = type
      if (timeout !== undefined) state.previewTimeout = timeout
    },
    // Export cuts mutations
    ADD_EXPORT_CUT(state, cut) {
      state.exportCuts.push({
        id: Date.now().toString(),
        start: cut.start,
        end: cut.end
      })
    },
    REMOVE_EXPORT_CUT(state, cutId) {
      state.exportCuts = state.exportCuts.filter(c => c.id !== cutId)
    },
    UPDATE_EXPORT_CUT(state, { id, start, end }) {
      const cut = state.exportCuts.find(c => c.id === id)
      if (cut) {
        cut.start = start
        cut.end = end
      }
    },
    CLEAR_EXPORT_CUTS(state) {
      state.exportCuts = []
    },
    REORDER_EXPORT_CUTS(state, { fromIndex, toIndex }) {
      const cut = state.exportCuts.splice(fromIndex, 1)[0]
      state.exportCuts.splice(toIndex, 0, cut)
    },
    ADD_METADATA_ITEM(state, item) {
      // Add to timeline metadata
      if (!state.metadata.find(m => m.id === item.id)) {
        state.metadata.push(item)
      }
      // Add to top of list (it's newest) - only if live
      if (state.isLive && !state.metadataList.find(m => m.id === item.id)) {
        state.metadataList.unshift(item)
        state.metadataNewest = item.timestamp
        state.metadataTotalCount++
      }
      
      // If live, set this as active (like original handleMetadata)
      if (state.isLive) {
        state.activeMetadataId = item.id
      }
    }
  },

  actions: {
    // Fetch timeline with buffer like original
    async fetchTimelineData({ commit, state }) {
      try {
        const buffer = Math.max(state.viewDuration, SIX_HOURS)
        const start = state.viewStart - buffer
        const end = state.viewEnd + buffer
        const data = await fetchTimeline(start, end)
        commit('SET_SEGMENTS', data.segments || [])
        // Merge metadata for timeline rendering
        commit('MERGE_METADATA', data.metadata || [])
      } catch (err) {
        console.error('[Store] fetchTimelineData error:', err)
      }
    },

    // Fetch metadata page - matches original fetchMetadataPage
    async fetchMetadataPageAction({ commit, state }, { direction = null, aroundTimestamp = null } = {}) {
      if (state.metadataLoading) return
      if (direction === 'older' && !state.metadataHasMore) return
      if (direction === 'newer' && !state.metadataHasMoreNewer) return
      
      commit('SET_METADATA_LOADING', true)
      
      try {
        let data
        if (aroundTimestamp) {
          // Fetch metadata centered around a specific timestamp
          data = await fetchMetadataPage(null, { around: aroundTimestamp, limit: 50 })
          commit('SET_METADATA_LIST', data.metadata || [])
          commit('SET_METADATA_PAGINATION', {
            hasMore: data.hasMoreOlder || data.hasMore || false,
            hasMoreNewer: data.hasMoreNewer || false,
            totalCount: data.totalCount
          })
          
          // Update active metadata to match the timestamp
          if (data.metadata && data.metadata.length > 0) {
            const sortedMeta = [...data.metadata].sort((a, b) => b.timestamp - a.timestamp)
            let activeMeta = null
            for (const m of sortedMeta) {
              if (aroundTimestamp >= m.timestamp) {
                activeMeta = m
                break
              }
            }
            if (activeMeta) {
              commit('SET_ACTIVE_METADATA_ID', activeMeta.id)
            } else {
              // If timestamp is before all metadata, use the oldest one
              commit('SET_ACTIVE_METADATA_ID', sortedMeta[sortedMeta.length - 1].id)
            }
          }
        } else if (direction === 'newer' && state.metadataNewest) {
          data = await fetchMetadataPage('newer', { after: state.metadataNewest, limit: 50 })
          const newItems = data.metadata || []
          commit('PREPEND_METADATA_NEWER', newItems)
          // If we got no results or fewer than limit, there's no more newer data
          commit('SET_METADATA_PAGINATION', { 
            hasMoreNewer: newItems.length > 0 && (data.hasMoreNewer || data.hasMore || false)
          })
        } else if (direction === 'older' && state.metadataOldest) {
          data = await fetchMetadataPage('older', { before: state.metadataOldest, limit: 50 })
          const oldItems = data.metadata || []
          commit('APPEND_METADATA_OLDER', oldItems)
          // If we got no results or fewer than limit, there's no more older data
          commit('SET_METADATA_PAGINATION', { 
            hasMore: oldItems.length > 0 && (data.hasMoreOlder || data.hasMore || false)
          })
        } else {
          // Initial load from current time
          data = await fetchMetadataPage(null, { before: Date.now(), limit: 50 })
          commit('SET_METADATA_LIST', data.metadata || [])
          commit('SET_METADATA_PAGINATION', {
            hasMore: data.hasMore,
            hasMoreNewer: false,
            totalCount: data.totalCount
          })
        }
        
        commit('UPDATE_METADATA_TIMESTAMPS')
      } catch (err) {
        console.error('[Store] fetchMetadataPageAction error:', err)
      } finally {
        commit('SET_METADATA_LOADING', false)
      }
    },

    // Go to timestamp - matches original gotoTimestamp exactly
    async gotoTimestamp({ commit, state, dispatch }, timestamp) {
      // Skip audioController's active metadata updates during navigation
      commit('SET_SKIP_ACTIVE_METADATA_UPDATE', true)
      
      // Don't go to future
      const now = Date.now()
      if (timestamp > now) {
        timestamp = now
      }
      
      // Capture current view range BEFORE updating state
      const prevViewStart = state.viewStart
      const prevViewEnd = state.viewEnd

      // Update state - SET LIVE TO FALSE FIRST, unlock view so it centers on new position
      commit('SET_LIVE', false)
      commit('SET_VIEW_LOCKED', false)
      commit('SET_CURRENT_TIME', timestamp)

      // Clear stale segments only when jumping far outside the current loaded range —
      // dragging/scrubbing stays within the buffered range so no clear needed.
      // Threshold: if the target timestamp is more than 1 view-duration outside previous view.
      const jumpThreshold = state.viewDuration * 1.5
      const withinLoadedRange = timestamp >= prevViewStart - jumpThreshold &&
                                timestamp <= prevViewEnd + jumpThreshold
      if (!withinLoadedRange) {
        commit('SET_SEGMENTS', [])
      }
      
      // Fetch timeline data for the new view range
      await dispatch('fetchTimelineData')
      
      // Fetch fresh metadata list around the timestamp
      await dispatch('fetchMetadataPageAction', { aroundTimestamp: timestamp })
      
      // Find the metadata that contains this timestamp
      const sortedMeta = [...state.metadataList].sort((a, b) => b.timestamp - a.timestamp)
      let activeMeta = null
      for (const m of sortedMeta) {
        if (timestamp >= m.timestamp) {
          activeMeta = m
          break
        }
      }
      if (activeMeta) {
        commit('SET_ACTIVE_METADATA_ID', activeMeta.id)
      } else if (state.metadataList.length > 0) {
        commit('SET_ACTIVE_METADATA_ID', state.metadataList[0].id)
      }
      
      // Re-enable active metadata updates before seeking
      commit('SET_SKIP_ACTIVE_METADATA_UPDATE', false)
      
      // Use audioController for seeking (handles play state internally)
      await audioController.seekTo(timestamp)
    },

    // Navigate to track - like gotoTimestamp but without metadata refetch
    // Used when clicking a track that's already in the list
    async navigateToTrack({ commit, state, dispatch }, timestamp) {
      // Skip audioController's active metadata updates during navigation
      commit('SET_SKIP_ACTIVE_METADATA_UPDATE', true)
      
      // Don't go to future
      const now = Date.now()
      if (timestamp > now) {
        timestamp = now
      }

      // Pre-fetch timeline segments for the target range BEFORE shifting the view.
      // SET_CURRENT_TIME updates viewStart/viewEnd synchronously, which immediately
      // triggers Timeline's render(). If segments aren't loaded yet the canvas draws
      // blank until the async fetch completes. Fetching first ensures segments are
      // already in the store before the view moves, so the first render draws correctly.
      try {
        const buffer = Math.max(state.viewDuration, 6 * 60 * 60 * 1000)
        const data = await fetchTimeline(
          timestamp - state.viewDuration * 0.5 - buffer,
          timestamp + state.viewDuration * 0.5 + buffer
        )
        commit('SET_SEGMENTS', data.segments || [])
        commit('MERGE_METADATA', data.metadata || [])
      } catch (err) {
        console.error('[Store] navigateToTrack prefetch error:', err)
      }

      // Now shift the view — segments already loaded so render draws immediately
      commit('SET_LIVE', false)
      commit('SET_VIEW_LOCKED', false)
      commit('SET_CURRENT_TIME', timestamp)
      
      // Re-enable active metadata updates before seeking
      commit('SET_SKIP_ACTIVE_METADATA_UPDATE', false)
      
      // Use audioController for seeking (handles play state internally)
      await audioController.seekTo(timestamp)
    },

    // Skip to next track (older in time, since list is newest-first)
    async skipToNextTrack({ state, commit, dispatch }) {
      if (state.isLive || !state.activeMetadataId) return
      
      // Sort metadata by timestamp descending (newest first)
      const sortedMeta = [...state.metadataList].sort((a, b) => b.timestamp - a.timestamp)
      const currentIndex = sortedMeta.findIndex(m => m.id === state.activeMetadataId)
      
      if (currentIndex === -1) return
      
      // Check if we need to load more older tracks
      if (currentIndex >= sortedMeta.length - 1) {
        // At the oldest loaded track - try to load more
        if (state.metadataHasMore && !state.metadataLoading) {
          await dispatch('fetchMetadataPageAction', { direction: 'older' })
          // Re-sort after loading
          const newSortedMeta = [...state.metadataList].sort((a, b) => b.timestamp - a.timestamp)
          const newIndex = newSortedMeta.findIndex(m => m.id === state.activeMetadataId)
          if (newIndex < newSortedMeta.length - 1) {
            const nextTrack = newSortedMeta[newIndex + 1]
            commit('SET_ACTIVE_METADATA_ID', nextTrack.id)
            await dispatch('navigateToTrack', nextTrack.timestamp)
          }
        }
        return
      }
      
      const nextTrack = sortedMeta[currentIndex + 1]
      commit('SET_ACTIVE_METADATA_ID', nextTrack.id)
      await dispatch('navigateToTrack', nextTrack.timestamp)
    },

    // Skip to previous track (newer in time, since list is newest-first)
    async skipToPreviousTrack({ state, commit, dispatch }) {
      if (state.isLive || !state.activeMetadataId) return
      
      // Sort metadata by timestamp descending (newest first)
      const sortedMeta = [...state.metadataList].sort((a, b) => b.timestamp - a.timestamp)
      const currentIndex = sortedMeta.findIndex(m => m.id === state.activeMetadataId)
      
      if (currentIndex === -1) return
      
      // Check if we need to load more newer tracks
      if (currentIndex <= 0) {
        // At the newest loaded track - try to load more
        if (state.metadataHasMoreNewer && !state.metadataLoading) {
          await dispatch('fetchMetadataPageAction', { direction: 'newer' })
          // Re-sort after loading
          const newSortedMeta = [...state.metadataList].sort((a, b) => b.timestamp - a.timestamp)
          const newIndex = newSortedMeta.findIndex(m => m.id === state.activeMetadataId)
          if (newIndex > 0) {
            const prevTrack = newSortedMeta[newIndex - 1]
            commit('SET_ACTIVE_METADATA_ID', prevTrack.id)
            await dispatch('navigateToTrack', prevTrack.timestamp)
          }
        }
        return
      }
      
      const prevTrack = sortedMeta[currentIndex - 1]
      commit('SET_ACTIVE_METADATA_ID', prevTrack.id)
      await dispatch('navigateToTrack', prevTrack.timestamp)
    },

    // Go live - matches original goLive exactly
    async goLive({ commit, state, dispatch }) {
      const wasPlaying = state.isPlaying
      
      // Skip active metadata updates during transition (prevents playhead jump)
      commit('SET_SKIP_ACTIVE_METADATA_UPDATE', true)
      
      commit('SET_LIVE', true)
      commit('SET_CURRENT_TIME', Date.now())
      
      // Fetch timeline data (don't await - fire and forget like original)
      dispatch('fetchTimelineData')
      
      // Fetch fresh metadata list from current time
      await dispatch('fetchMetadataPageAction', {})
      
      // Set active metadata to most recent
      if (state.metadataList.length > 0) {
        commit('SET_ACTIVE_METADATA_ID', state.metadataList[0].id)
      }
      
      // Re-enable active metadata updates
      commit('SET_SKIP_ACTIVE_METADATA_UPDATE', false)
      
      // Use audioController for live playback (cleans up Web Audio state)
      audioController.playLive()
      
      // Pause if wasn't playing before
      if (!wasPlaying) {
        const audio = document.getElementById('audio-player')
        if (audio) audio.pause()
      }
    },

    setViewDuration({ commit, dispatch }, duration) {
      commit('SET_VIEW', { duration })
      dispatch('fetchTimelineData')
    },
    
    toggleSelectionMode({ commit, state, getters }) {
      if (!state.selectionMode && !getters.canEnterSelectionMode) return false
      commit('SET_SELECTION_MODE', !state.selectionMode)
      if (!state.selectionMode) commit('CLEAR_SELECTION')
      return true
    },
    
    startSelection({ commit }, timestamp) {
      commit('SET_SELECTION', { start: timestamp, end: timestamp })
      commit('SET_IS_SELECTING', true)
    },
    
    updateSelection({ commit }, timestamp) {
      commit('SET_SELECTION', { end: timestamp })
    },
    
    endSelection({ commit, state }) {
      commit('SET_IS_SELECTING', false)
      if (state.selectionStart > state.selectionEnd) {
        commit('SET_SELECTION', { start: state.selectionEnd, end: state.selectionStart })
      }
    },
    
    nudgeSelection({ commit, state }, { target, delta }) {
      if (state.selectionStart === null || state.selectionEnd === null) return
      const start = Math.min(state.selectionStart, state.selectionEnd)
      const end = Math.max(state.selectionStart, state.selectionEnd)
      const now = Date.now()
      if (target === 'start') {
        let newStart = start + delta
        newStart = Math.min(newStart, end - 1000)
        newStart = Math.min(newStart, now)
        commit('SET_SELECTION', { start: newStart, end })
      } else {
        let newEnd = end + delta
        newEnd = Math.max(newEnd, start + 1000)
        newEnd = Math.min(newEnd, now)
        commit('SET_SELECTION', { start, end: newEnd })
      }
    },
    
    clearSelection({ commit, state }) {
      const currentTime = state.currentTime
      const sortedMeta = [...state.metadataList].sort((a, b) => b.timestamp - a.timestamp)
      for (const m of sortedMeta) {
        if (currentTime >= m.timestamp) {
          commit('SET_ACTIVE_METADATA_ID', m.id)
          break
        }
      }
      commit('CLEAR_SELECTION')
    }
  },
  modules: {
    auth
  }
})
